import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { MenuButton, SingleMenuButton, ParentMenuButton } from '../../services/types.js';
import styles from '../../styles/mariana-normal-buttons.css?inline';

/**
 * Menu Buttons Component
 * Renders hierarchical menu buttons with local navigation.
 * - parent_button: navigates to submenu locally (no message sent)
 * - single_button: dispatches button-click event to send message to agent
 */
@customElement('mariana-menu-buttons')
export class MarianaMenuButtons extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Array })
  buttons: MenuButton[] = [];

  /** When set (on locked/reconnected menus), auto-navigate to the submenu containing this value. */
  @property({ type: String })
  selectedValue = '';

  @state()
  private navigationStack: { label: string; buttons: MenuButton[] }[] = [];

  @state()
  private hasAutoNavigated = false;

  /**
   * Find the path to a value in the menu tree.
   * Returns the stack of parent buttons to navigate through, or null if not found.
   */
  private findPathToValue(buttons: MenuButton[], value: string): ParentMenuButton[] | null {
    for (const button of buttons) {
      if (button.type === 'single_button' && button.value === value) {
        return []; // Found at this level
      }
      if (button.type === 'parent_button') {
        const childPath = this.findPathToValue(button.value, value);
        if (childPath !== null) {
          return [button, ...childPath];
        }
      }
    }
    return null;
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    // Auto-navigate to the correct submenu when selectedValue is set (reconnect scenario)
    if (!this.hasAutoNavigated && this.selectedValue && this.buttons.length > 0) {
      const path = this.findPathToValue(this.buttons, this.selectedValue);
      if (path && path.length > 0) {
        this.navigationStack = path.map(parent => ({
          label: parent.label,
          buttons: parent.value,
        }));
      }
      this.hasAutoNavigated = true;
    }
  }

  private get currentButtons(): MenuButton[] {
    if (this.navigationStack.length === 0) {
      return this.buttons;
    }
    return this.navigationStack[this.navigationStack.length - 1].buttons;
  }

  private get currentLabel(): string | null {
    if (this.navigationStack.length === 0) {
      return null;
    }
    return this.navigationStack[this.navigationStack.length - 1].label;
  }

  private handleParentClick(button: ParentMenuButton) {
    this.navigationStack = [
      ...this.navigationStack,
      { label: button.label, buttons: button.value },
    ];
  }

  private handleSingleClick(button: SingleMenuButton) {
    this.dispatchEvent(new CustomEvent('button-click', {
      detail: { label: button.label, value: button.value },
      bubbles: true,
      composed: true,
    }));
  }

  private handleBack() {
    if (this.navigationStack.length > 0) {
      this.navigationStack = this.navigationStack.slice(0, -1);
    }
  }

  render() {
    if (!this.buttons || this.buttons.length === 0) {
      return html``;
    }

    const currentButtons = this.currentButtons;
    const showBack = this.navigationStack.length > 0;

    return html`
      <div class="button-container">
        ${showBack ? html`
          <div class="menu-header">
            <button class="menu-back-button" @click=${this.handleBack} type="button">
              <span class="menu-back-arrow"></span>
              ${this.currentLabel}
            </button>
          </div>
        ` : ''}
        ${currentButtons.map((button) => {
          if (button.type === 'parent_button') {
            return html`
              <button
                class="dynamic-button"
                @click=${() => this.handleParentClick(button)}
                type="button"
              >
                ${button.label}
              </button>
            `;
          }
          return html`
            <button
              class="dynamic-button"
              @click=${() => this.handleSingleClick(button)}
              type="button"
            >
              ${button.label}
            </button>
          `;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mariana-menu-buttons': MarianaMenuButtons;
  }
}
